import { GeckoMeasurementUnit } from 'types'

import {
  CitedNote,
  FACT_HELP,
  MATERIAL_GROUP_META,
  OptionMeta,
  optionsOf,
  REGION_META,
  SHELL_GEOMETRY_META,
} from './ffsClassificationContent'
import { ClassificationFacts, RegionKind } from './ffsComponentClassification'

/**
 * How the FFS pre-planning step RENDERS each classification fact: the prompt, the
 * input kind, and (for enum facts) the selectable options plus their per-option
 * help.
 *
 * This is the React-facing metadata that `ffsComponentClassification.ts` claims
 * to keep "separate from the rules" — it now genuinely is, so that module stays
 * pure, React-free classification logic. Wording changes live here and in
 * `ffsClassificationContent.ts`; neither can affect the rules.
 *
 * Nothing here imports React either: it is plain data the step interprets.
 */

/**
 * Discriminated on `kind` so `options`/`optionHelp` exist exactly for `enum`
 * facts and are REQUIRED there — which is why the consumer needs no
 * `key === 'region' ? … : …` branching to find option help.
 *
 * `options` and `optionHelp` are both sourced from ONE option-meta record per
 * enum (`REGION_META`, …), so an option cannot exist without its cited help —
 * coverage is guaranteed at the definition site rather than by this type.
 *
 * `optionHelp` stays keyed by `string` on purpose: the consumer looks help up by
 * a runtime value it cannot narrow, so `CitedNote | undefined` is the honest
 * result there. Parameterizing this over each fact's own value union was tried
 * and rejected — the consumer indexes with `keyof ClassificationFacts`, which
 * widens the mapped type back to the union of arms and only type-checks with a
 * cast. Not worth a cast to restate a guarantee the data already provides.
 *
 * `options` is a mutable `Array` (not `ReadonlyArray`) because antd's `Select`
 * demands one. These are module-level constants built once by `optionsOf`, so
 * nothing mutates them and no per-render copy is needed.
 */
export type ClassificationQuestionMeta = {
  prompt: string
  /** Plain-language, cited explanation of the question itself. */
  help: CitedNote
} & (
  | {
      kind: 'enum'
      options: Array<{ value: string; label: string }>
      /**
       * Total over the fact's OWN value union, which is not always the same as
       * `options` — `region` offers a subset. Values here carry a `label` as
       * well as the citation, so the consumer can render a saved value that is
       * no longer offered without it degrading to a raw slug. Every record
       * assigned here is already a `Record<K, OptionMeta>`, so this is the
       * accurate type rather than a widened one.
       */
      optionHelp: Readonly<Record<string, OptionMeta>>
    }
  | { kind: 'boolean' }
  | {
      kind: 'number'
      /**
       * Canonical unit the fact is STORED and COMPARED in (the classifier reads
       * these values directly). When present, `ClassificationFactInput` renders
       * and edits in the user's preferred unit and converts back to this unit on
       * change, so stored values stay canonical. Omitted for dimensionless facts
       * (NPS is a nominal size designation; stiffener ratio is a ratio).
       */
      canonicalUnit?: GeckoMeasurementUnit
    }
)

/**
 * The region kinds the workflow currently OFFERS.
 *
 * The inspection data visualization only places indications on shell bodies, so
 * the other thirteen `REGION_KINDS` describe geometry an LTA cannot sit on
 * today. Presenting them invites a choice this workflow cannot support.
 *
 * RETAINED BUT NOT OFFERED, deliberately: every other kind keeps its
 * `REGION_META` entry and cited help, its `B2_REGIONS` / `C_REGIONS` branch in
 * `classifyComponentType`, and its slot in the persisted Zod enum. A saved
 * record carrying one still parses, still classifies correctly, and still
 * renders with its proper label (`ClassificationFactInput` re-adds the current
 * value to the list when it is not offered). The values are live code, just not
 * on the menu — widening this set is all it takes to bring them back.
 */
const SUPPORTED_REGION_KINDS: ReadonlySet<RegionKind> = new Set([
  'remote-shell-body',
])

/**
 * Prompts + render metadata for each fact, keyed by the fact union so a missing
 * or misspelled entry fails compilation (and so consumers can iterate the keys
 * without casting). The classifier decides which facts are actually asked, via
 * `ClassificationResult['missing']`.
 */
export const CLASSIFICATION_UI: Record<
  keyof ClassificationFacts,
  ClassificationQuestionMeta
> = {
  region: {
    // Not "Where is…" — with one supported region this is a statement of what
    // the assessment covers, not a question about location.
    prompt: 'Assessment region:',
    help: FACT_HELP.region,
    kind: 'enum',
    options: optionsOf(REGION_META).filter((o) =>
      SUPPORTED_REGION_KINDS.has(o.value)
    ),
    // Stays TOTAL over `REGION_KINDS`, not narrowed alongside `options`: help
    // must still resolve for a saved value that is no longer offered.
    optionHelp: REGION_META,
  },
  headIs2to1Elliptical: {
    prompt: 'Is the head a 2:1 elliptical head?',
    help: FACT_HELP.headIs2to1Elliptical,
    kind: 'boolean',
  },
  onCylinderSide: {
    prompt: 'Is the assessment region on the cylinder side of the junction?',
    help: FACT_HELP.onCylinderSide,
    kind: 'boolean',
  },
  pressureOnlyLoading: {
    prompt: 'Is the loading pressure-only?',
    help: FACT_HELP.pressureOnlyLoading,
    kind: 'boolean',
  },
  stiffenerRatio: {
    prompt: 'Stiffening-ring attachment ratio (Fig 4.18):',
    help: FACT_HELP.stiffenerRatio,
    kind: 'number',
  },
  shellGeometry: {
    prompt: 'Shell geometry:',
    help: FACT_HELP.shellGeometry,
    kind: 'enum',
    options: optionsOf(SHELL_GEOMETRY_META),
    optionHelp: SHELL_GEOMETRY_META,
  },
  Lss: {
    prompt: 'Shell span Lss:',
    help: FACT_HELP.Lss,
    kind: 'number',
    canonicalUnit: GeckoMeasurementUnit.Meters,
  },
  H: {
    prompt: 'Shell height H:',
    help: FACT_HELP.H,
    kind: 'number',
    canonicalUnit: GeckoMeasurementUnit.Meters,
  },
  D: {
    prompt: 'Diameter D:',
    help: FACT_HELP.D,
    kind: 'number',
    canonicalUnit: GeckoMeasurementUnit.Meters,
  },
  materialGroup: {
    prompt: 'Piping material group:',
    help: FACT_HELP.materialGroup,
    kind: 'enum',
    options: optionsOf(MATERIAL_GROUP_META),
    optionHelp: MATERIAL_GROUP_META,
  },
  nps: {
    prompt: 'Nominal pipe size (NPS, inches):',
    help: FACT_HELP.nps,
    kind: 'number',
  },
  designTempC: {
    prompt: 'Design temperature:',
    help: FACT_HELP.designTempC,
    kind: 'number',
    canonicalUnit: GeckoMeasurementUnit.Celsius,
  },
  pressureGovernsThickness: {
    prompt: 'Does internal pressure/fill govern the required thickness?',
    help: FACT_HELP.pressureGovernsThickness,
    kind: 'boolean',
  },
  supplementalLoadsGovern: {
    prompt: 'Do supplemental loads govern?',
    help: FACT_HELP.supplementalLoadsGovern,
    kind: 'boolean',
  },
  hasCodeDesignEquation: {
    prompt: 'Is there a code design equation relating pressure to thickness?',
    help: FACT_HELP.hasCodeDesignEquation,
    kind: 'boolean',
  },
  codeAssessmentProcedureAvailable: {
    prompt: 'Is a code assessment procedure available for this region?',
    help: FACT_HELP.codeAssessmentProcedureAvailable,
    kind: 'boolean',
  },
}

/**
 * The fixed order facts are presented in — the declaration order of
 * `CLASSIFICATION_UI`. Typed via the fact union, so no cast is needed at the
 * call site.
 */
export const FACT_ORDER = Object.keys(CLASSIFICATION_UI) as ReadonlyArray<
  keyof ClassificationFacts
>
